import { createReducer } from '@reduxjs/toolkit';
import {
  getAccountSessionAndLimitEndpoint,
  getGameBreakPeriodsEndpoint,
  getSelfLimitationPendingResourceId,
  getSelfLimitationPurposeResourceId,
  getSelfLimitationResourceId,
  getSelfLimitationTypeSuitedResourceId,
  getSummaryGameBreakEndpoint,
  getUserCommunicationsResourceId,
} from 'services/playResponsibly/playResponsibly.config';
import {
  CompartmentType,
  FeedbackMessage,
  GameLimitsList,
  PausaDalGiocoState,
  SelfLimitationObject,
  SelfLimitationPending,
  SelfLimitationPurpose,
  SelfLimitationTypeSuited,
  UserCommunication,
} from 'services/playResponsibly/playResponsibly.types';
import { apiRequestSuccess } from '../api/api.actions';
import {
  clearFeedbackMessage,
  setFeedbackMessage,
  startLoadingPostSelfLimitation,
  stopLoadingPostSelfLimitation,
} from './playResponsibly.actions';

const initialState = {
  userCommunications: [] as UserCommunication[],
  pausaDalGioco: {} as PausaDalGiocoState,
  gameLimitsList: {} as GameLimitsList,
  selfLimitation: [] as SelfLimitationObject[],
  selfLimitationPending: {} as SelfLimitationPending | null,
  selfLimitationPurpose: {} as SelfLimitationPurpose | null,
  selfLimitationTypeSuited: {} as SelfLimitationTypeSuited,
  selfLimitationPostLoading: false,
};

const playResponsiblyReducer = createReducer(initialState, (builder) => {
  builder.addCase(apiRequestSuccess, (state, action) => {
    const { payload } = action;

    if (payload.resourceId === getUserCommunicationsResourceId) {
      const communications = payload.response.data.communications as
        | UserCommunication[]
        | undefined;

      if (Array.isArray(communications)) {
        state.userCommunications = communications;
      }
      return;
    }

    if (payload.resourceId === getSummaryGameBreakEndpoint) {
      const tempCompartments: CompartmentType[] = [
        ...payload.response.data.compartments.reverse(),
      ];
      state.pausaDalGioco.compartments = tempCompartments.map((compartment) => {
        return {
          ...compartment,
          gameBreaks: [
            ...compartment.gameBreaks.map((gameBreak) => {
              return {
                ...gameBreak,
                type:
                  !gameBreak.active &&
                  !gameBreak.startDate &&
                  gameBreak.type === 0
                    ? -1
                    : gameBreak.type,
              };
            }),
          ],
        };
      });
      return;
    }

    if (payload.resourceId === getGameBreakPeriodsEndpoint) {
      //This puts the permanent type at the end of the array from the beginning
      payload.response.data.typological?.push(
        payload.response.data.typological?.shift(),
      );
      state.pausaDalGioco.typological = payload.response.data.typological;
      return;
    }

    if (payload.resourceId === getAccountSessionAndLimitEndpoint) {
      state.gameLimitsList = payload.response.data.gameLimitsList;
      return;
    }

    if (payload.resourceId === getSelfLimitationResourceId) {
      state.selfLimitation =
        payload.response.data.responseObject.selfLimitationList;
      return;
    }

    if (payload.resourceId === getSelfLimitationPendingResourceId) {
      state.selfLimitationPending = payload.response.data.responseObject;
      return;
    }

    if (payload.resourceId === getSelfLimitationPurposeResourceId) {
      state.selfLimitationPurpose = payload.response.data.responseObject;
      return;
    }

    if (payload.resourceId === getSelfLimitationTypeSuitedResourceId) {
      state.selfLimitationTypeSuited =
        payload.response.data.responseObject.typeList[0];
      return;
    }
  });

  builder.addCase(setFeedbackMessage, (state, { payload }) => {
    state.pausaDalGioco.feedbackMessage = payload;
  });
  builder.addCase(clearFeedbackMessage, (state, action) => {
    state.pausaDalGioco.feedbackMessage = {} as FeedbackMessage;
  });
  builder.addCase(startLoadingPostSelfLimitation, (state) => {
    state.selfLimitationPostLoading = true;
  });
  builder.addCase(stopLoadingPostSelfLimitation, (state) => {
    state.selfLimitationPostLoading = false;
  });
});

export default playResponsiblyReducer;
